import React, { useState } from "react";
import "./LoginSignUp.css";
import { auth, provider, facebookProvider } from "../../config/firebase";
import { signInWithPopup } from "firebase/auth";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { config } from "../../config/const";
import glogo from "../assets/glogo.png";

export const LoginSignUp = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState({ username: "", email: "", phone: "", password: "" });

  // State for email prompt modal (when Facebook doesn't return email)
  const [pendingFbLogin, setPendingFbLogin] = useState(null); // { displayName, idToken }
  const [fbEmailInput, setFbEmailInput] = useState("");
  const [signingIn, setSigningIn] = useState(false); // prevent double-click

  useEffect(() => {
    const token = localStorage.getItem("idToken");
    if (token) navigate("/");
  }, []);

  const handleFacebookSign = async () => {
    if (signingIn) return;
    setSigningIn(true);
    signInWithPopup(auth, facebookProvider)
      .then((result) => {
        const fbUser = result.user;
        const { displayName, email } = fbUser;
        const uid = fbUser.uid; // Firebase UID — stable across devices and sessions
        fbUser.getIdToken().then(async (idToken) => {
          if (!email) {
            // 1. Check localStorage cache first (fast, offline-safe)
            const savedEmail =
              localStorage.getItem("fb_saved_email") ||
              localStorage.getItem("email");
            if (savedEmail && savedEmail !== "null" && savedEmail !== "undefined") {
              localStorage.setItem("idToken", idToken);
              localStorage.setItem("email", savedEmail);
              localStorage.setItem("displayName", displayName);
              sendUserDataToBackend(displayName, savedEmail, idToken);
              return;
            }

            // 2. Check database — email is stored there after the first login on any device
            try {
              const res = await axios.get(`${config.backend_url}/user/email-by-uid?uid=${uid}`);
              if (res.data?.email) {
                const dbEmail = res.data.email;
                localStorage.setItem("idToken", idToken);
                localStorage.setItem("email", dbEmail);
                localStorage.setItem("fb_saved_email", dbEmail);
                localStorage.setItem("displayName", displayName);
                sendUserDataToBackend(displayName, dbEmail, idToken);
                return;
              }
            } catch (_) {
              // 404 means first time — fall through to show modal
            }

            // 3. First time ever — ask the user once, then store in DB + localStorage
            setPendingFbLogin({ displayName, idToken, uid });
            return;
          }
          localStorage.setItem("idToken", idToken);
          localStorage.setItem("email", email);
          localStorage.setItem("displayName", displayName);
          sendUserDataToBackend(displayName, email, idToken);
        });
        toast.success("Signed In Successfully", {
          position: "top-center", autoClose: 1000, hideProgressBar: true,
          closeOnClick: true, pauseOnHover: false, draggable: false, theme: "dark",
        });
      })
      .catch((error) => {
        if (error.code !== "auth/cancelled-popup-request") {
          console.error("Facebook sign-in failed:", error);
          toast.error("Facebook Sign In Unsuccessful", {
            position: "top-center", autoClose: 1000, hideProgressBar: true,
            closeOnClick: true, pauseOnHover: false, draggable: false, theme: "dark",
          });
        }
      })
      .finally(() => setSigningIn(false));
  };

  const handleFbEmailSubmit = () => {
    if (!fbEmailInput || !fbEmailInput.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    const { displayName, idToken } = pendingFbLogin;
    localStorage.setItem("idToken", idToken);
    localStorage.setItem("email", fbEmailInput);
    localStorage.setItem("fb_saved_email", fbEmailInput); // persists across logouts
    localStorage.setItem("displayName", displayName);
    setPendingFbLogin(null);
    sendUserDataToBackend(displayName, fbEmailInput, idToken);
  };

  const handleGoogleSign = async () => {
    if (signingIn) return;
    setSigningIn(true);
    signInWithPopup(auth, provider)
      .then((result) => {
        const fbUser = result.user;
        const { displayName, email } = fbUser;
        fbUser.getIdToken().then((idToken) => {
          localStorage.setItem("idToken", idToken);
          localStorage.setItem("email", email);
          localStorage.setItem("displayName", displayName);
          sendUserDataToBackend(displayName, email, idToken);
        });
        toast.success("Signed In Successfully", {
          position: "top-center", autoClose: 1000, hideProgressBar: true,
          closeOnClick: true, pauseOnHover: false, draggable: false, theme: "dark",
        });
      })
      .catch((error) => {
        if (error.code !== "auth/cancelled-popup-request") {
          console.error("Sign-in failed:", error);
          toast.error("Sign In Unsuccessful", {
            position: "top-center", autoClose: 1000, hideProgressBar: true,
            closeOnClick: true, pauseOnHover: false, draggable: false, theme: "dark",
          });
        }
      })
      .finally(() => setSigningIn(false));
  };

  const sendUserDataToBackend = (name, email, idToken) => {
    console.log("Sending data to backend");
    axios
      .post(`${config.backend_url}/login`, { name, email, idToken }, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        mode: "cors",
      })
      .then((response) => {
        if (response.status === 200 || response.status === 201) navigate("/");
        console.log("User data sent to backend successfully");
      })
      .catch((error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.message || error?.message || "Unknown error";
        console.error("Error sending user data to backend:", status, detail, error);
        toast.error(`Login failed (${status || "network"}): ${detail}`, {
          position: "top-center", autoClose: 4000, hideProgressBar: true,
          closeOnClick: true, pauseOnHover: true, draggable: false, theme: "dark",
        });
      });
  };

  return (
    <div className="container_">
      <div className="header">
        <div>
          <div className="text">Login</div>
          <div className="underline"></div>
        </div>
      </div>

      {/* Email prompt modal for Facebook users without email */}
      {pendingFbLogin && (
        <div className="fb-email-overlay">
          <div className="fb-email-modal">
            <h3>One more step</h3>
            <p>Facebook didn't share your email. Please enter it to continue.</p>
            <input
              type="email"
              placeholder="your@email.com"
              value={fbEmailInput}
              onChange={(e) => setFbEmailInput(e.target.value)}
              className="fb-email-input"
              onKeyDown={(e) => e.key === "Enter" && handleFbEmailSubmit()}
              autoFocus
            />
            <button onClick={handleFbEmailSubmit} className="fb-email-submit">Continue</button>
            <button onClick={() => setPendingFbLogin(null)} className="fb-email-cancel">Cancel</button>
          </div>
        </div>
      )}

      <div className="inputs_">
        <div className="social-buttons-container">
          <div className="google-container">
            <button onClick={handleGoogleSign} className="google-button" disabled={signingIn} style={{ opacity: signingIn ? 0.6 : 1 }}>
              <img src={glogo} alt="Google Icon" />
            </button>
          </div>
          <div className="facebook-container">
            <button onClick={handleFacebookSign} className="facebook-button" disabled={signingIn} style={{ opacity: signingIn ? 0.6 : 1 }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28">
                <path fill="#1877F2" d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.514c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};
